import { formatCell } from './numbers';

/**
 * Parse a single CSV line handling quoted values and escaped quotes
 *
 * @param {string} line - CSV line to parse
 * @returns {string[]} Array of parsed values
 *
 * @example
 * parseCSVLine('value1,"quoted, value",value3')
 * // Returns: ['value1', 'quoted, value', 'value3']
 */
export const parseCSVLine = line => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                // Escaped quote - add one quote and skip next
                current += '"';
                i++;
            } else {
                // Toggle quote state
                inQuotes = !inQuotes;
            }
        } else if (char === ',' && !inQuotes) {
            // End of field (comma outside quotes)
            values.push(current);
            current = '';
        } else {
            current += char;
        }
    }

    // Push the last field
    values.push(current);
    return values;
};

// Empty cell = undefined, null, or blank string (rendered as N/A in grids).
export const isEmptyCellValue = value => value === undefined || value === null || String(value).trim() === '';

/**
 * Detect if a string value should be treated as numeric
 *
 * @param {*} value - Value to check
 * @returns {boolean} True if value should be treated as a number
 *
 * @example
 * isNumeric('123')     // true
 * isNumeric('123.45')  // true
 * isNumeric('00123')   // false (leading zero - likely an ID)
 * isNumeric('abc')     // false
 */
export const isNumeric = value => {
    if (typeof value === 'number') return true;
    if (typeof value !== 'string') return false;
    if (value.trim() === '') return false;

    // Don't treat values with leading zeros as numbers (like IDs: "00123")
    if (value.length > 1 && value[0] === '0' && value[1] !== '.') return false;

    return !isNaN(value) && !isNaN(parseFloat(value));
};

/**
 * Parse CSV content into AG-Grid compatible format
 *
 * @param {string} csvContent - CSV content with headers in first row
 * @param {Object} options - Parsing options
 * @param {boolean} options.autoDetectNumbers - Convert numeric strings to numbers (default: true)
 * @param {boolean} options.includeColumnDefs - Generate AG-Grid column definitions (default: true)
 * @returns {Object} Object with rowData and columnDefs arrays
 *
 * @example
 * const result = parseCSVToGrid(`
 * Name,Age,Score
 * John,25,95.5
 * Jane,30,88.0
 * `);
 * // Returns:
 * // {
 * //   rowData: [
 * //     { Name: 'John', Age: 25, Score: 95.5 },
 * //     { Name: 'Jane', Age: 30, Score: 88.0 }
 * //   ],
 * //   columnDefs: [...]
 * // }
 */
export const parseCSVToGrid = (csvContent, options = {}) => {
    const { autoDetectNumbers = true, includeColumnDefs = true, columnMetadata = null } = options;

    const lines = csvContent.split('\n').filter(line => line.trim());

    if (lines.length === 0) {
        return { rowData: [], columnDefs: [] };
    }

    // Parse header row
    const headers = parseCSVLine(lines[0]);

    // Parse data rows
    const rowData = [];
    for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        const row = {};

        headers.forEach((header, index) => {
            const value = values[index] || '';
            // Convert numeric strings to numbers for proper sorting
            row[header] = autoDetectNumbers && isNumeric(value) ? parseFloat(value) : value;
        });

        rowData.push(row);
    }

    // Generate column definitions if requested
    const columnDefs = includeColumnDefs
        ? headers.map((header, index) => {
              const meta = columnMetadata ? columnMetadata[index] : null;
              const headerName = meta?.display_name || header;
              const displayFormat = meta?.display_format || null;
              // Empty cells render as N/A in the grid only; the underlying row
              // values stay raw so CSV export keeps them empty.
              const valueFormatter = ({ value }) => {
                  if (isEmptyCellValue(value)) {
                      return 'N/A';
                  }
                  return displayFormat ? formatCell(value, displayFormat) : String(value);
              };
              return {
                  field: header,
                  headerName,
                  colId: header,
                  sortable: true,
                  filter: true,
                  resizable: true,
                  type: 'dimension', // TODO
                  valueFormatter,
              };
          })
        : [];

    return { rowData, columnDefs };
};

/**
 * Parse CSV content into simple array of objects (no Grid-specific formatting)
 *
 * @param {string} csvContent - CSV content with headers in first row
 * @param {boolean} autoDetectNumbers - Convert numeric strings to numbers (default: true)
 * @returns {Object[]} Array of row objects
 */
export const parseCSVToJSON = (csvContent, autoDetectNumbers = true) => {
    const { rowData } = parseCSVToGrid(csvContent, {
        autoDetectNumbers,
        includeColumnDefs: false,
    });
    return rowData;
};
